import { Box, CircularProgress, Typography } from '@mui/material'
import { Page } from 'Common/Page/Page'
import { PageHeader } from 'Common/PageHeader/PageHeader'
import { Input, Button, DropDown, CalendarInput } from 'Common/Supplyvue-ui'
import { useState } from 'react'
import {
  useCreateProjectData,
  useSnapshotData,
  useSourceSites,
} from './CreateProject.data'
import { WorkcentreModal } from '../WorkcentreModal/WorkcentreModal.view'
import {
  SNAPSHOT_LENGTH_MENU_ITEMS,
  disableWeekdays,
  isValidDate,
  getSelectSnapshotDropdownMenuItems,
  getLastSunday,
} from './CreateProject.utils'
import { SourceSiteDetails } from '../WorkcentreModal/WorkcentreModal.types'
import { ProjectParamsInterface } from './CreateProject.types'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

export const projectParams = {
  projectName: '',
  snapshotName: '',
  selectedSnapshot: '',
  snapshotEndDate: getLastSunday(),
  snapshotLength: 0,
  workcentres: [],
}

export const CreateProjectPage = () => {
  const [projectParamsState, setProjectParamsState] =
    useState<ProjectParamsInterface>(projectParams)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isLoading: isSnapshotDataLoading, data: snapshotData } =
    useSnapshotData()

  const navigate = useNavigate()
  const { data: wcs, isLoading: isWcsLoading } = useSourceSites()

  const handleProjectNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      projectName: event.target.value,
    })
  }

  const handleSelectedSnapshotChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      selectedSnapshot: event.target.value as string,
    })
  }

  const handleSnapshotNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      snapshotName: event.target.value,
    })
  }

  const handleSnapshotLengthChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setProjectParamsState({
      ...projectParamsState,
      snapshotLength: event.target.value as number,
    })
  }

  const handleEndDateChange = (date: Date | null) => {
    if (isValidDate(date) && date) {
      setProjectParamsState({
        ...projectParamsState,
        snapshotEndDate: date,
      })
    }
  }

  const handleOkbuttonClick = (selectedWorkcentres: SourceSiteDetails[]) => {
    setIsModalOpen(false)
    setProjectParamsState({
      ...projectParamsState,
      workcentres: selectedWorkcentres,
    })
  }

  const onCreateProjectSuccess = (projectId: string, message: string) => {
    toast.success(message)
    navigate('/')
  }
  const { isPending: isCreatingProject, mutate: createProject } =
    useCreateProjectData({ onCreateProjectSuccess })

  const handleCreateProject = () => {
    createProject(projectParamsState)
  }

  const isCreateProjectButtonDisabled = () => {
    if (projectParamsState.projectName === '') {
      return true
    }

    if (projectParamsState.selectedSnapshot === '') {
      return (
        projectParamsState.snapshotName === '' ||
        projectParamsState.snapshotLength === 0 ||
        projectParamsState.snapshotEndDate === null ||
        projectParamsState.workcentres.length === 0
      )
    }

    return false
  }

  const handleClearButtonClick = () => {
    setProjectParamsState({
      ...projectParams,
    })
    setIsModalOpen(false)
  }
  //   console.log('isCreateProjectButtonDisabled : ', isCreateProjectButtonDisabled)

  return (
    <Page>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        sx={{ paddingTop: '36px' }}
      >
        <Box
          display="flex"
          width="100%"
          height="100%"
          justifyContent="flex-start"
          alignItems="center"
          flexDirection="column"
          sx={{ paddingTop: '240px' }}
        >
          <Box width={400} display="flex" flexDirection="column">
            {/* Project name  */}
            <Box marginBottom={2}>
              <Typography
                variant="subtitle1"
                sx={{
                  marginBottom: '8px',
                }}
              >
                Project name
              </Typography>
              <Input
                fullWidth
                value={projectParamsState.projectName}
                onChange={handleProjectNameChange}
                //   onKeyDown={handleOnKeyDown}
                disabled={isSnapshotDataLoading}
                //   ariaLabel={projectNameLabel}
              />
            </Box>
            {/* End project name */}
            {/* Snapshot start */}
            <Box>
              <Box marginBottom={2}>
                <Typography
                  sx={{
                    marginBottom: '8px',
                  }}
                  variant="subtitle1"
                >
                  Select snapshot
                </Typography>
                <DropDown
                  fullWidth
                  label="Select snapshot"
                  sx={{
                    height: '56px',
                  }}
                  //className={classes.selectSnapshot}
                  defaultLabel="Import new snapshot"
                  menuItems={
                    getSelectSnapshotDropdownMenuItems(snapshotData) ?? []
                  }
                  value={projectParamsState.selectedSnapshot}
                  onChange={handleSelectedSnapshotChange}
                  disabled={isSnapshotDataLoading}
                  MenuProps={{
                    sx: {
                      height: '300px',
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                />
              </Box>

              <Box sx={{ marginTop: '20px' }} marginBottom={2}>
                <Typography
                  sx={{
                    marginBottom: '8px',
                  }}
                  variant="subtitle1"
                >
                  Snapshot name
                </Typography>
                <Input
                  placeholder="Enter snapshot name"
                  fullWidth
                  value={projectParamsState.snapshotName}
                  onChange={handleSnapshotNameChange}
                  disabled={
                    projectParamsState.selectedSnapshot !== '' ||
                    isSnapshotDataLoading
                  }
                  ariaLabel="Snapshot name"
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box width="45%">
                  <Typography
                    sx={{
                      marginBottom: '8px',
                    }}
                    variant="subtitle1"
                  >
                    End date
                  </Typography>
                  <CalendarInput
                    format="dd/MM/yyyy"
                    sx={{
                      minWidth: '250px',
                      marginRight: '30px',
                    }}
                    iconLabel="End date"
                    value={projectParamsState.snapshotEndDate}
                    maxDate={new Date()}
                    onChange={handleEndDateChange}
                    disabled={
                      projectParamsState.selectedSnapshot !== '' ||
                      isSnapshotDataLoading
                    }
                    shouldDisableDate={disableWeekdays}
                  />
                </Box>
                <Box width="45%">
                  <Typography
                    sx={{
                      marginBottom: '8px',
                    }}
                    variant="subtitle1"
                  >
                    Snapshot length
                  </Typography>
                  <DropDown
                    label="Snapshot length"
                    fullWidth
                    sx={{
                      height: '56px',
                    }}
                    //className={classes.select}
                    menuItems={SNAPSHOT_LENGTH_MENU_ITEMS}
                    value={projectParamsState.snapshotLength}
                    onChange={handleSnapshotLengthChange}
                    disabled={
                      projectParamsState.selectedSnapshot !== '' ||
                      isSnapshotDataLoading
                    }
                  />
                </Box>
              </Box>
            </Box>
            {/* Snapshot end */}
            {/* start workcentres */}
            <Box marginTop={2}>
              <Button
                onClick={() => {
                  setIsModalOpen(true)
                }}
                variant="contained"
                disabled={
                  projectParamsState.projectName === '' ||
                  projectParamsState.selectedSnapshot !== '' ||
                  isSnapshotDataLoading ||
                  isWcsLoading
                }
              >
                Select workcentres
              </Button>
            </Box>
            {/* End workcentres */}
            <Box
              marginTop={2}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Box sx={{ marginRight: '10px' }}>
                <Button
                  onClick={handleClearButtonClick}
                  // className={classes.modalActionButton}
                  // label="Cancel"
                  // disabled={isActionDisabled}
                  variant="outlined"
                >
                  Clear
                </Button>
              </Box>
              <Box>
                <Button
                  onClick={handleCreateProject}
                  // className={classes.modalActionButton}
                  // label={actionButtonLabel}
                  disabled={isCreateProjectButtonDisabled()}
                  variant="contained"
                >
                  {isCreatingProject && (
                    <CircularProgress
                      color="inherit"
                      size={16}
                      sx={{
                        marginRight: '8px',
                      }}
                    />
                  )}
                  Create project
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <WorkcentreModal
        isUpdating={false}
        isModalOpen={isModalOpen}
        handleModalClose={() => {
          setIsModalOpen(false)
        }}
        sourceSiteData={wcs ?? []}
        handleOkbuttonClick={handleOkbuttonClick}
        readOnly={false}
      />
    </Page>
  )
}
